import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api } from "../lib/api.js";
import type { Project, ProjectSettings as Settings, ProjectStatus } from "../lib/types.js";
import { Modal } from "../components/Modal.js";
import { useToast } from "../components/Toast.js";
import { Icon, type IconName } from "../components/Icon.js";
import { DeleteBookDialog } from "./DeleteBookDialog.js";
import { CoverArt } from "../library/CoverArt.js";
import type { ModelTask } from "../lib/types.js";
import { TASK_LABEL, listRememberedModels, clearRememberedModel, clearAllRememberedModels } from "../ai/catalog.js";

type BlueprintHealth = "connected" | "auth-failed" | "unreachable" | "ollama-unavailable";

function blueprintStatusLabel(status: BlueprintHealth | undefined): string {
  switch (status) {
    case "connected": return "Connected";
    case "auth-failed": return "Authentication failed";
    case "ollama-unavailable": return "Ollama unavailable";
    case "unreachable": return "Blueprint unreachable";
    default: return "Unknown";
  }
}

function blueprintStatusColor(status: BlueprintHealth | undefined): string {
  return status === "connected" ? "var(--success, #2e7d32)" : status ? "var(--danger, #c0392b)" : "var(--muted, #888)";
}

/**
 * Read an image File and return a downscaled JPEG data URL suitable for a book
 * cover (portrait, long edge ~1200px). Keeps the stored payload small so it fits
 * comfortably under the API's coverImage size cap.
 */
async function fileToCoverDataUrl(file: File): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Could not read the image file."));
    reader.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error("That file is not a readable image."));
    el.src = dataUrl;
  });
  const MAX = 1200;
  let { width, height } = img;
  if (width > MAX || height > MAX) {
    const scale = MAX / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", 0.82);
}

const STATUSES: ProjectStatus[] = ["planning", "drafting", "revising", "editing", "complete"];

type TabKey = "general" | "series" | "goals" | "ai" | "organization" | "export" | "backup" | "advanced";
const TABS: { key: TabKey; label: string; desc: string; icon: IconName }[] = [
  { key: "general", label: "General", desc: "Title, cover, description", icon: "file" },
  { key: "series", label: "Series", desc: "Series details and ordering", icon: "layers" },
  { key: "goals", label: "Goals", desc: "Word count and deadlines", icon: "target" },
  { key: "ai", label: "AI & Writing", desc: "Model, per-action defaults", icon: "sparkles" },
  { key: "organization", label: "Organization", desc: "Tags, custom fields", icon: "note" },
  { key: "export", label: "Export & Publishing", desc: "Formats and metadata", icon: "download" },
  { key: "backup", label: "Backup & Sync", desc: "Autosave and backups", icon: "history" },
  { key: "advanced", label: "Advanced", desc: "Workspace behavior", icon: "settings" }
];

// Autosave presets (ms). "Custom" reveals the exact slider.
const AUTOSAVE_PRESETS: { key: string; label: string; ms: number }[] = [
  { key: "fast", label: "Fast", ms: 1000 },
  { key: "balanced", label: "Balanced", ms: 2000 },
  { key: "relaxed", label: "Relaxed", ms: 4000 }
];

interface Props {
  project: Project;
  settings: Settings;
  onClose: () => void;
  onSaved: (project: Project) => void;
  onDeleted: () => void;
}

export function ProjectSettingsModal({ project, settings, onClose, onSaved, onDeleted }: Props) {
  const toast = useToast();
  const [tab, setTab] = useState<TabKey>("general");
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [form, setForm] = useState({
    title: project.title,
    subtitle: project.subtitle ?? "",
    author: project.author ?? "",
    genre: project.genre ?? "",
    description: project.description ?? "",
    status: project.status,
    targetWordCount: project.targetWordCount?.toString() ?? "",
    targetCompletionDate: project.targetCompletionDate ?? "",
    seriesName: project.seriesName ?? "",
    seriesNumber: project.seriesNumber?.toString() ?? "",
    seriesDescription: project.seriesDescription ?? "",
    volumeTitle: project.volumeTitle ?? "",
    dailyWordGoal: String(settings.dailyWordGoal ?? 0)
  });
  const [seriesEnabled, setSeriesEnabled] = useState(!!project.seriesName);
  // coverImage: `undefined` = unchanged; `null` = remove; string = new image.
  const [coverImage, setCoverImage] = useState<string | null | undefined>(undefined);
  const effectiveCover = coverImage === undefined ? project.coverImage ?? null : coverImage;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [autosaveInterval, setAutosaveInterval] = useState(settings.autosaveIntervalMs);
  const [autosaveCustom, setAutosaveCustom] = useState(!AUTOSAVE_PRESETS.some((p) => p.ms === settings.autosaveIntervalMs));
  const [aiProvider, setAiProvider] = useState(settings.aiProvider ?? "");
  const [aiModel, setAiModel] = useState(settings.aiModel ?? "");
  const [aiTemperature, setAiTemperature] = useState<number | null>(settings.aiTemperature ?? null);
  const [providers, setProviders] = useState<string[]>([]);
  const [groqModels, setGroqModels] = useState<string[]>([]);
  const [togetherModels, setTogetherModels] = useState<string[]>([]);
  const [togetherError, setTogetherError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [bpStatus, setBpStatus] = useState<
    { status: BlueprintHealth; detail: string; model?: string } | null
  >(null);
  const [bpModels, setBpModels] = useState<{ name: string; size: number | null }[]>([]);
  const [bpTesting, setBpTesting] = useState(false);
  const [bpLoadingModels, setBpLoadingModels] = useState(false);

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // Snapshot of the original values, to detect unsaved changes and to revert.
  const initial = useMemo(
    () => JSON.stringify({
      ...form_snapshot(project, settings),
      coverChanged: false
    }),
    [project, settings]
  );
  const current = JSON.stringify({
    title: form.title, subtitle: form.subtitle, author: form.author, genre: form.genre,
    description: form.description, status: form.status, targetWordCount: form.targetWordCount,
    targetCompletionDate: form.targetCompletionDate, seriesEnabled,
    seriesName: form.seriesName, seriesNumber: form.seriesNumber, seriesDescription: form.seriesDescription,
    volumeTitle: form.volumeTitle,
    dailyWordGoal: form.dailyWordGoal, autosave: autosaveInterval,
    aiProvider, aiModel, aiTemperature, coverChanged: coverImage !== undefined
  });
  const dirty = initial !== current;

  const pickCover = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    try { setCoverImage(await fileToCoverDataUrl(file)); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Could not load that image."); }
  };

  useEffect(() => {
    api.aiStatus(project.id)
      .then((s) => { setProviders(s.providers); setGroqModels(s.groq?.models ?? []); setTogetherModels(s.together?.models ?? []); })
      .catch(() => { setProviders([]); setGroqModels([]); setTogetherModels([]); });
  }, [project.id]);

  const refreshBlueprintStatus = async () => {
    setBpTesting(true);
    try { const s = await api.blueprintStatus(project.id); setBpStatus({ status: s.status, detail: s.detail, model: s.model }); }
    catch { setBpStatus({ status: "unreachable", detail: "Could not reach the server." }); }
    finally { setBpTesting(false); }
  };
  const refreshBlueprintModels = async () => {
    setBpLoadingModels(true);
    try {
      const r = await api.blueprintModels(project.id);
      setBpModels(r.models);
      if (!aiModel && r.configuredModel) setAiModel(r.configuredModel);
    } catch { setBpModels([]); }
    finally { setBpLoadingModels(false); }
  };

  useEffect(() => {
    if (aiProvider !== "blueprint") return;
    void refreshBlueprintStatus();
    void refreshBlueprintModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiProvider, project.id]);

  useEffect(() => {
    if (aiProvider !== "together") return;
    setTogetherError(null);
    api.togetherModels(project.id)
      .then((r) => {
        if (r.models.length > 0) setTogetherModels(r.models);
        if (aiModel && !r.configuredModelAvailable) setTogetherError(`Together does not offer "${aiModel}" on this account. Pick an available model below.`);
      })
      .catch((e) => setTogetherError(e instanceof Error ? e.message : "Could not load Together's model list."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiProvider, project.id]);

  const resetToSaved = () => {
    setForm({
      title: project.title, subtitle: project.subtitle ?? "", author: project.author ?? "",
      genre: project.genre ?? "", description: project.description ?? "", status: project.status,
      targetWordCount: project.targetWordCount?.toString() ?? "",
      targetCompletionDate: project.targetCompletionDate ?? "",
      seriesName: project.seriesName ?? "", seriesNumber: project.seriesNumber?.toString() ?? "",
      seriesDescription: project.seriesDescription ?? "", volumeTitle: project.volumeTitle ?? "",
      dailyWordGoal: String(settings.dailyWordGoal ?? 0)
    });
    setSeriesEnabled(!!project.seriesName);
    setCoverImage(undefined);
    setAutosaveInterval(settings.autosaveIntervalMs);
    setAutosaveCustom(!AUTOSAVE_PRESETS.some((p) => p.ms === settings.autosaveIntervalMs));
    setAiProvider(settings.aiProvider ?? "");
    setAiModel(settings.aiModel ?? "");
    setAiTemperature(settings.aiTemperature ?? null);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("A title is required."); setTab("general"); return; }
    setSaving(true);
    try {
      const targetWords = form.targetWordCount.trim() ? Math.max(0, Number.parseInt(form.targetWordCount, 10) || 0) : null;
      const seriesNum = form.seriesNumber.trim() ? Math.max(0, Number.parseInt(form.seriesNumber, 10) || 0) : null;
      const useSeries = seriesEnabled && form.seriesName.trim().length > 0;
      const { project: updated } = await api.updateProject(project.id, {
        title: form.title.trim(),
        subtitle: form.subtitle.trim() || null,
        author: form.author.trim() || null,
        genre: form.genre.trim() || null,
        description: form.description.trim() || null,
        status: form.status,
        targetWordCount: targetWords,
        targetCompletionDate: form.targetCompletionDate.trim() || null,
        seriesName: useSeries ? form.seriesName.trim() : null,
        seriesNumber: useSeries ? seriesNum : null,
        seriesDescription: useSeries ? (form.seriesDescription.trim() || null) : null,
        volumeTitle: useSeries ? (form.volumeTitle.trim() || null) : null,
        ...(coverImage !== undefined ? { coverImage } : {})
      });
      await api.updateSettings(project.id, {
        autosaveIntervalMs: autosaveInterval,
        aiProvider: aiProvider || null,
        aiModel: aiModel.trim() || null,
        aiTemperature,
        dailyWordGoal: Math.max(0, Number.parseInt(form.dailyWordGoal, 10) || 0)
      });
      toast.success("Project settings saved.");
      onSaved(updated);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const descLen = form.description.length;

  return (
    <Modal
      title="Project Settings"
      subtitle="Manage your book's details, goals, AI preferences, and workspace behavior."
      onClose={onClose}
      width={940}
      footer={
        <>
          <button className="btn btn-ghost" style={{ marginRight: "auto" }} onClick={resetToSaved} disabled={saving || !dirty} title="Discard unsaved changes and restore saved values">
            <Icon name="undo" size={15} /> Reset to defaults
          </button>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving || !dirty}>
            {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
        </>
      }
    >
      <div className="pset">
        <nav className="pset-nav">
          {TABS.map((t) => (
            <button key={t.key} className={`pset-tab ${t.key === tab ? "active" : ""}`} onClick={() => setTab(t.key)}>
              <Icon name={t.icon} size={16} className="pset-tab-ico" />
              <span className="pset-tab-text">
                <span className="pset-tab-label">{t.label}</span>
                <span className="pset-tab-desc">{t.desc}</span>
              </span>
              {t.key === tab && <Icon name="chevronRight" size={14} className="pset-tab-caret" />}
            </button>
          ))}
        </nav>

        <div className="pset-main">
          {tab === "general" && (
            <PsetSection icon="file" title="General Information" desc="Basic details about your book and how it appears across ClovaScribe.">
              <div className="pset-general">
                <div className="pset-cover">
                  <div className="pset-cover-art">
                    <CoverArt project={{ ...project, title: form.title || project.title, author: form.author || project.author, coverImage: effectiveCover }} />
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }}
                    onChange={(e) => { void pickCover(e.target.files?.[0]); e.target.value = ""; }} />
                  <button type="button" className="btn btn-sm" onClick={() => fileInputRef.current?.click()}>
                    <Icon name="image" size={14} /> {effectiveCover ? "Change cover" : "Upload cover"}
                  </button>
                  {effectiveCover && (
                    <button type="button" className="btn btn-sm btn-ghost btn-danger" onClick={() => setCoverImage(null)}>
                      <Icon name="trash" size={14} /> Remove cover
                    </button>
                  )}
                  <p className="faint pset-cover-hint">Recommended 1600 × 2560 (1.6:1)</p>
                </div>

                <div className="grow col gap-3" style={{ minWidth: 0 }}>
                  <div className="field">
                    <label className="label">Title <span className="pset-req">*</span></label>
                    <input className="input" value={form.title} onChange={(e) => set("title", e.target.value)} />
                  </div>
                  <div className="field">
                    <label className="label">Subtitle</label>
                    <input className="input" value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="Optional subtitle…" />
                  </div>
                  <div className="pset-row">
                    <div className="grow field">
                      <label className="label">Author <span className="pset-req">*</span></label>
                      <input className="input" value={form.author} onChange={(e) => set("author", e.target.value)} />
                    </div>
                    <div className="grow field">
                      <label className="label">Genre</label>
                      <input className="input" value={form.genre} onChange={(e) => set("genre", e.target.value)} placeholder="e.g. Contemporary Romance" />
                    </div>
                  </div>
                  <div className="pset-row">
                    <div className="grow field">
                      <label className="label">Status</label>
                      <select className="select" value={form.status} onChange={(e) => set("status", e.target.value)}>
                        {STATUSES.map((s) => <option key={s} value={s}>{s[0]!.toUpperCase() + s.slice(1)}</option>)}
                      </select>
                    </div>
                    <div className="grow" />
                  </div>
                  <div className="field">
                    <label className="label">Description / Synopsis</label>
                    <textarea className="textarea" value={form.description} onChange={(e) => set("description", e.target.value)} rows={6}
                      placeholder="A short synopsis. Scribe AI can use this as project context." />
                    <div className="pset-count"><span className={descLen > 2000 ? "pset-over" : "faint"}>{descLen.toLocaleString()} / 2,000 characters</span></div>
                  </div>
                </div>
              </div>
            </PsetSection>
          )}

          {tab === "series" && (
            <PsetSection icon="layers" title="Series Information" desc="Group this book into a series (optional).">
              <div className="pset-toggle-row">
                <div>
                  <div className="strong" style={{ fontSize: 13.5 }}>This book belongs to a series</div>
                  <div className="faint" style={{ fontSize: 12 }}>Turn on to add series name, number, and description.</div>
                </div>
                <button type="button" className={`pset-switch ${seriesEnabled ? "on" : ""}`} role="switch" aria-checked={seriesEnabled}
                  onClick={() => setSeriesEnabled((v) => !v)}><span /></button>
              </div>
              {seriesEnabled && (
                <div className="col gap-3" style={{ marginTop: 14 }}>
                  <div className="pset-row">
                    <div className="grow field">
                      <label className="label">Series name</label>
                      <input className="input" value={form.seriesName} onChange={(e) => set("seriesName", e.target.value)} placeholder="e.g. The Together Saga" />
                    </div>
                    <div style={{ width: 120 }} className="field">
                      <label className="label">Book #</label>
                      <input className="input" inputMode="numeric" value={form.seriesNumber}
                        onChange={(e) => set("seriesNumber", e.target.value.replace(/[^0-9]/g, ""))} placeholder="1" />
                    </div>
                  </div>
                  <div className="field">
                    <label className="label">Volume title <span className="faint">(what this volume is called)</span></label>
                    <input className="input" value={form.volumeTitle} onChange={(e) => set("volumeTitle", e.target.value)}
                      placeholder="e.g. US" />
                  </div>
                  <div className="field">
                    <label className="label">Series description <span className="faint">(optional)</span></label>
                    <textarea className="textarea" value={form.seriesDescription} onChange={(e) => set("seriesDescription", e.target.value)} rows={3}
                      placeholder="What ties the books in this series together." />
                  </div>
                </div>
              )}
            </PsetSection>
          )}

          {tab === "goals" && (
            <PsetSection icon="target" title="Writing Goals" desc="Targets to keep your draft moving — motivation, not accounting.">
              <div className="pset-row">
                <div className="grow field">
                  <label className="label">Target word count</label>
                  <input className="input" inputMode="numeric" value={form.targetWordCount}
                    onChange={(e) => set("targetWordCount", e.target.value.replace(/[^0-9]/g, ""))} placeholder="e.g. 90000" />
                </div>
                <div className="grow field">
                  <label className="label">Target completion date</label>
                  <input className="input" type="date" value={form.targetCompletionDate} onChange={(e) => set("targetCompletionDate", e.target.value)} />
                </div>
              </div>
              <div className="field">
                <label className="label">Daily word goal</label>
                <input className="input" inputMode="numeric" value={form.dailyWordGoal}
                  onChange={(e) => set("dailyWordGoal", e.target.value.replace(/[^0-9]/g, ""))} placeholder="e.g. 500" />
                <p className="faint" style={{ fontSize: 11.5, margin: "6px 0 0" }}>
                  Drives the daily streak and progress on your Overview dashboard.
                </p>
              </div>
            </PsetSection>
          )}

          {tab === "ai" && (
            <PsetSection icon="sparkles" title="AI & Writing" desc="Scribe AI provider, model, and the defaults it uses per action.">
              <div className="pset-row">
                <div className="grow field">
                  <label className="label">Scribe AI provider</label>
                  <select className="select" value={aiProvider} onChange={(e) => setAiProvider(e.target.value)}>
                    <option value="">Default</option>
                    {providers.map((p) => <option key={p} value={p}>{p[0]!.toUpperCase() + p.slice(1)}</option>)}
                  </select>
                  <p className="faint" style={{ fontSize: 10.5, margin: "4px 0 0" }}>Keys are configured server-side and never sent to the browser.</p>
                </div>
                <div className="grow field">
                  <label className="label">Model (optional)</label>
                  {aiProvider === "blueprint" && bpModels.length > 0 ? (
                    <select className="select" value={aiModel} onChange={(e) => setAiModel(e.target.value)}>
                      {aiModel && !bpModels.some((m) => m.name === aiModel) && <option value={aiModel}>{aiModel} (configured)</option>}
                      {bpModels.map((m) => <option key={m.name} value={m.name}>{m.name}</option>)}
                    </select>
                  ) : aiProvider === "groq" && groqModels.length > 0 ? (
                    <select className="select" value={aiModel} onChange={(e) => setAiModel(e.target.value)}>
                      <option value="">Default (openai/gpt-oss-20b)</option>
                      {aiModel && !groqModels.includes(aiModel) && <option value={aiModel}>{aiModel} (configured)</option>}
                      {groqModels.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  ) : aiProvider === "together" && togetherModels.length > 0 ? (
                    <select className="select" value={aiModel} onChange={(e) => setAiModel(e.target.value)}>
                      <option value="">Default (openai/gpt-oss-120b)</option>
                      {aiModel && !togetherModels.includes(aiModel) && <option value={aiModel}>{aiModel} (configured)</option>}
                      {togetherModels.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  ) : (
                    <input className="input" value={aiModel} onChange={(e) => setAiModel(e.target.value)} placeholder="provider default" />
                  )}
                  {aiProvider === "together" && togetherError && (
                    <p style={{ fontSize: 10.5, margin: "4px 0 0", color: "var(--danger, #c0392b)" }}>{togetherError}</p>
                  )}
                </div>
              </div>

              {aiProvider === "blueprint" && (
                <div className="pset-subcard">
                  <div className="row gap-2" style={{ alignItems: "center", justifyContent: "space-between" }}>
                    <span className="label" style={{ margin: 0 }}>Blueprint connection</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: blueprintStatusColor(bpStatus?.status) }}>
                      {bpTesting ? "Checking…" : blueprintStatusLabel(bpStatus?.status)}
                    </span>
                  </div>
                  {bpStatus?.detail && <p className="faint" style={{ fontSize: 11, margin: "6px 0 0" }}>{bpStatus.detail}</p>}
                  <div className="row gap-2" style={{ marginTop: 10 }}>
                    <button type="button" className="btn btn-sm" onClick={() => void refreshBlueprintStatus()} disabled={bpTesting}>Test connection</button>
                    <button type="button" className="btn btn-sm" onClick={() => void refreshBlueprintModels()} disabled={bpLoadingModels}>
                      {bpLoadingModels ? "Loading…" : "Refresh models"}
                    </button>
                  </div>
                  <p className="faint" style={{ fontSize: 10.5, margin: "8px 0 0" }}>
                    The Blueprint URL and API key are configured server-side (BLUEPRINT_URL / BLUEPRINT_API_KEY) and never sent to the browser.
                  </p>
                </div>
              )}

              <div className="pset-divide" />
              <RememberedModelDefaults projectId={project.id} />
            </PsetSection>
          )}

          {tab === "organization" && (
            <PsetSection icon="note" title="Organization" desc="How this book's knowledge is tagged and grouped.">
              <div className="pset-empty">
                <Icon name="note" size={26} />
                <h4>Tags &amp; custom fields live on entries</h4>
                <p className="muted">Characters, locations, story-bible entries and research each carry their own tags and fields — edit them on the item. Project-wide organization settings will appear here as they're added.</p>
              </div>
            </PsetSection>
          )}

          {tab === "export" && (
            <PsetSection icon="download" title="Export & Publishing" desc="Metadata embedded when you export or publish this book.">
              <div className="pset-meta-grid">
                <MetaRow label="Title" value={form.title || "—"} />
                <MetaRow label="Subtitle" value={form.subtitle || "—"} />
                <MetaRow label="Author" value={form.author || "—"} />
                <MetaRow label="Genre" value={form.genre || "—"} />
                {seriesEnabled && form.seriesName.trim() && (
                  <MetaRow label="Series" value={`${form.seriesName}${form.seriesNumber ? ` #${form.seriesNumber}` : ""}`} />
                )}
                {seriesEnabled && form.volumeTitle.trim() && (
                  <MetaRow label="Volume" value={form.volumeTitle} />
                )}
              </div>
              <p className="faint" style={{ fontSize: 11.5, margin: "12px 0 0" }}>
                These come from the General and Series tabs and are written into exported ePub / PDF / DOCX files.
                Recommended cover: 1600 × 2560 (1.6:1). Choose a format and run an export from the <strong>Export</strong> button in the toolbar.
              </p>
            </PsetSection>
          )}

          {tab === "backup" && (
            <PsetSection icon="history" title="Backup & Sync" desc="How often your manuscript autosaves as you write.">
              <div className="field">
                <label className="label">Autosave after you stop typing</label>
                <div className="pset-presets">
                  {AUTOSAVE_PRESETS.map((p) => (
                    <button key={p.key} type="button"
                      className={`pset-preset ${!autosaveCustom && autosaveInterval === p.ms ? "active" : ""}`}
                      onClick={() => { setAutosaveInterval(p.ms); setAutosaveCustom(false); }}>
                      <span className="pset-preset-label">{p.label}</span>
                      <span className="pset-preset-sub">{(p.ms / 1000).toFixed(p.ms % 1000 ? 1 : 0)}s</span>
                    </button>
                  ))}
                  <button type="button" className={`pset-preset ${autosaveCustom ? "active" : ""}`} onClick={() => setAutosaveCustom(true)}>
                    <span className="pset-preset-label">Custom</span>
                    <span className="pset-preset-sub">exact</span>
                  </button>
                </div>
                {autosaveCustom && (
                  <div style={{ marginTop: 14 }}>
                    <label className="label">Exact delay: {(autosaveInterval / 1000).toFixed(1)}s</label>
                    <input type="range" min={500} max={5000} step={250} value={autosaveInterval}
                      onChange={(e) => setAutosaveInterval(Number.parseInt(e.target.value, 10))}
                      style={{ width: "100%", accentColor: "var(--accent)" }} />
                  </div>
                )}
                <p className="faint" style={{ fontSize: 11.5, margin: "10px 0 0" }}>
                  Your work also saves on blur and when you leave a scene. Every save enters version history, so nothing is lost.
                </p>
              </div>
            </PsetSection>
          )}

          {tab === "advanced" && (
            <PsetSection icon="settings" title="Advanced" desc="Power-user and experimental behavior.">
              <div className="field">
                <label className="label">
                  AI creativity (temperature): {aiTemperature == null ? "Provider default" : aiTemperature.toFixed(2)}
                </label>
                <input type="range" min={0} max={1.5} step={0.05} value={aiTemperature ?? 0.7}
                  onChange={(e) => setAiTemperature(Number.parseFloat(e.target.value))}
                  style={{ width: "100%", accentColor: "var(--accent)" }} />
                <div className="pset-scale"><span>Precise</span><span>Balanced</span><span>Inventive</span></div>
                <div className="row gap-2" style={{ marginTop: 8 }}>
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => setAiTemperature(null)} disabled={aiTemperature == null}>
                    Use provider default
                  </button>
                </div>
                <p className="faint" style={{ fontSize: 11.5, margin: "8px 0 0" }}>
                  Higher values make Scribe AI more inventive and less predictable. Applies to generative actions (brainstorm, rewrite, continue).
                </p>
              </div>

              <div className="pset-danger">
                <div className="pset-danger-head">
                  <Icon name="alert" size={15} /> Danger zone
                </div>
                <div className="pset-danger-row">
                  <div style={{ minWidth: 0 }}>
                    <div className="strong" style={{ fontSize: 13.5 }}>Delete this book</div>
                    <div className="faint" style={{ fontSize: 12, lineHeight: 1.5 }}>
                      Permanently removes “{project.title}” and everything in it — chapters, scenes,
                      characters, relationships, notes, revisions, audiobook renders, and review markup.
                      This cannot be undone.
                    </div>
                  </div>
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => setDeleteOpen(true)}>
                    <Icon name="trash" size={14} /> Delete this book…
                  </button>
                </div>
              </div>
            </PsetSection>
          )}
        </div>
      </div>

      {deleteOpen && (
        <DeleteBookDialog
          project={project}
          onCancel={() => setDeleteOpen(false)}
          onDeleted={() => { setDeleteOpen(false); onDeleted(); }}
        />
      )}
    </Modal>
  );
}

/** The original editable values, for change detection. */
function form_snapshot(project: Project, settings: Settings) {
  return {
    title: project.title, subtitle: project.subtitle ?? "", author: project.author ?? "",
    genre: project.genre ?? "", description: project.description ?? "", status: project.status,
    targetWordCount: project.targetWordCount?.toString() ?? "",
    targetCompletionDate: project.targetCompletionDate ?? "",
    seriesEnabled: !!project.seriesName,
    seriesName: project.seriesName ?? "", seriesNumber: project.seriesNumber?.toString() ?? "",
    seriesDescription: project.seriesDescription ?? "",
    volumeTitle: project.volumeTitle ?? "",
    dailyWordGoal: String(settings.dailyWordGoal ?? 0),
    autosave: settings.autosaveIntervalMs, aiProvider: settings.aiProvider ?? "",
    aiModel: settings.aiModel ?? "", aiTemperature: settings.aiTemperature ?? null
  };
}

function PsetSection({ icon, title, desc, children }: { icon: IconName; title: string; desc: string; children: ReactNode }) {
  return (
    <div className="pset-pane">
      <div className="pset-pane-head">
        <span className="pset-pane-ico"><Icon name={icon} size={18} /></span>
        <div>
          <h3 className="pset-pane-title">{title}</h3>
          <p className="pset-pane-desc">{desc}</p>
        </div>
      </div>
      <div className="pset-pane-body">{children}</div>
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="pset-meta-row">
      <span className="pset-meta-label">{label}</span>
      <span className="pset-meta-value truncate">{value}</span>
    </div>
  );
}

/**
 * Lists the per-action AI models the user chose to "remember", with a way to reset
 * each (so the picker asks again next time) or clear them all. Stored per-viewer in
 * localStorage, so this manages the current browser's choices.
 */
function RememberedModelDefaults({ projectId }: { projectId: string }) {
  const [items, setItems] = useState<{ task: ModelTask; ref: { provider: string; model: string } }[]>(
    () => listRememberedModels(projectId)
  );
  return (
    <div className="field">
      <div className="row" style={{ alignItems: "center", justifyContent: "space-between" }}>
        <label className="label" style={{ margin: 0 }}>Per-action model defaults</label>
        {items.length > 0 && (
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => { clearAllRememberedModels(projectId); setItems([]); }}>Reset all</button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="faint" style={{ fontSize: 11.5, margin: "8px 0 0" }}>
          None remembered yet. When you run an AI action and tick “Remember my choice for this action”, it appears here as a row so you can change it later.
        </p>
      ) : (
        <div className="pset-models">
          {items.map(({ task, ref }) => (
            <div key={task} className="pset-model-row">
              <span className="pset-model-task">{TASK_LABEL[task]}</span>
              <span className="pset-model-name truncate">{ref.model}</span>
              <button type="button" className="btn btn-sm btn-ghost" title="Ask again next time"
                onClick={() => { clearRememberedModel(task, projectId); setItems((prev) => prev.filter((i) => i.task !== task)); }}>
                Reset
              </button>
            </div>
          ))}
        </div>
      )}
      <p className="faint" style={{ fontSize: 10.5, margin: "8px 0 0" }}>
        Remembered on this device. A row using the global default shows “Default”. Resetting one makes that action show the model picker again.
      </p>
    </div>
  );
}
